import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import { CompleteImpactAttributes } from '@loyalty/shared-common/event-processing-api-client';
import {
  CurrencyAttributes,
  ExpirationAttributes,
  ExpirationMode,
  ExpirationPeriodUnits,
  ExpirationType,
  LevelAttributes,
  LevelsConfigAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import {
  DeepPartial,
  defaultCurrencyImage,
  defaultLevelImage,
  defaultProfileImage,
  getNextExpirationDate,
  uuid,
} from '@loyalty/shared-common';
import { Leaderboard, LeaderboardEntry } from '@loyalty/shared-common/leaderboard-api-client';
import {
  MembershipAttributes,
  ProfileAttributes,
  ProfileIdentityKind,
  ProfileRewardAttributes,
  ProfileState,
  TransactionAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { RewardListRuleState } from '@loyalty/shared-common/rule-api-client';
import { RewardType } from '@loyalty/shared-common/rewards-api-client';

const profileId = '8875fda4-9d00-4330-942d-43775b778da1';
const accountId = '4bb76e86-b8d0-4449-9557-760a86b6350a';
const currencyId = '3972d214-ade4-46d7-a412-fbb57c23be5b';
export const programIdFixture = 'cbc07540-e90f-4c76-b607-616ab2eb1015';
const level1Id = 'bf683855-2534-4cdf-acc0-9bca2eadbec0';
const level2Id = 'd9994b6f-329c-41bf-9f3f-98d5cf3ac882';

export function buildProfileFixture(
  currencies = CurrenciesFixture,
  rewardsFixture: CompleteImpactAttributes = makeRewardsFixture(currencies),
): ProfileAttributes {
  return {
    id: profileId,
    accountId: accountId,
    state: ProfileState.ENABLED,
    firstName: 'David',
    lastName: 'Rossi',
    language: 'en',
    birthday: '1974-07-24',
    picture: defaultProfileImage,
    email: 'david.rossi@fbi.org',
    identities: [
      { kind: ProfileIdentityKind.email, value: 'david.rossi@fbi.org' },
      { kind: ProfileIdentityKind.referralCode, value: 'ABCD1234', programId: programIdFixture },
      { kind: ProfileIdentityKind.memberCode, value: 'm67u-4bur', programId: programIdFixture },
    ],
    currencies: currencies.map((currency, index) => ({
      id: `c887627f-e03d-4e0a-aac4-bc06582b4a6${index}`,
      currencyId: currency.id,
      programId: programIdFixture,
      value: 500,
      totalAccumulated: 800,
      totalOverCurrencyPeriod: 800,
      firstTimeEarned: new Date().toISOString(),
      nextExpiration: currency.expiration
        ? { amount: 500, date: getNextExpirationDate(currency.expiration, 'Europe/Brussels') }
        : undefined,
    })),
    memberships: [buildMembershipFixture(currencies[0]?.programId || uuid())],
    transactions: buildTransactionsFixture(currencies),
    rewards: rewardsFixture.rules.map((rule, index) => ({
      id: `cb9b954e-3dcf-4a80-8a85-5df79a9189f${index}`,
      promocode: `F000${(index + 1).toString(16)}`,
      type: RewardType.PROMOCODE,
      i18nAttributes: rule.reward?.i18nAttributes,
      amount: rule.currency?.value,
      currencyId: currencies[index % currencies.length]?.id,
      createdAt: subDays(new Date('2025-03-25T15:11:18.457Z'), index).toJSON(),
    })) as DeepPartial<ProfileRewardAttributes[]>,
  } as DeepPartial<ProfileAttributes> as ProfileAttributes;
}

export function buildMembershipFixture(programId: string): MembershipAttributes {
  return {
    id: uuid(),
    programId,
    blocked: false,
    lastVisit: new Date().toJSON(),
    consecutiveDailyVisits: 10,
    totalDailyVisits: 100,
    createdAt: '2021-10-01T00:00:00.000Z',
    level: {
      current: level1Id,
      acquiredAt: '2021-10-01T00:00:00.000Z',
    },
  };
}

export function buildLevelConfigFixture(options?: {
  currencies?: CurrencyAttributes[];
  withExpiration?: boolean;
}): LevelsConfigAttributes {
  const currencies = options?.currencies || CurrenciesFixture;

  const expiration: ExpirationAttributes = options?.withExpiration
    ? {
        type: ExpirationType.ROLLING,
        expirationMode: ExpirationMode.REEVALUATE,
        period: {
          unit: ExpirationPeriodUnits.YEAR,
          value: 1,
        },
      }
    : { type: ExpirationType.NONE };

  return {
    id: 'abd4f413-8289-4e54-ab61-36dc9a455f17',
    programId: currencies[0]?.programId || uuid(),
    timezone: 'utc',
    expiration,
    currencyId: currencies[0].id,
    currency: currencies[0],
  };
}

export const LevelsFixture: LevelAttributes[] = [
  {
    id: level1Id,
    minCurrencyAmount: 300,
    i18nAttributes: {
      en: {
        name: 'Level 1',
        image: defaultLevelImage,
      },
      fr: {
        name: 'Niveau 1',
        image: defaultLevelImage,
      },
      nl: {
        name: 'Niveau 1',
        image: defaultLevelImage,
      },
    },
  },
  {
    id: level2Id,
    minCurrencyAmount: 1000,
    i18nAttributes: {
      en: {
        name: 'Level 2',
        image: defaultLevelImage,
      },
      fr: {
        name: 'Niveau 2',
        image: defaultLevelImage,
      },
      nl: {
        name: 'Niveau 2',
        image: defaultLevelImage,
      },
    },
  },
] as DeepPartial<LevelAttributes>[] as LevelAttributes[];

export const CurrenciesFixture: CurrencyAttributes[] = [
  {
    id: currencyId,
    i18nAttributes: {
      en: {
        name: 'Points',
        image: defaultCurrencyImage,
      },
      fr: {
        name: 'Points',
        image: defaultCurrencyImage,
      },
      nl: {
        name: 'Punten',
        image: defaultCurrencyImage,
      },
    },
    expiration: {
      type: ExpirationType.ROLLING,
      period: {
        unit: ExpirationPeriodUnits.MONTH,
        value: 5,
      },
    },
  },
] as DeepPartial<CurrencyAttributes>[] as CurrencyAttributes[];

export function buildTransactionsFixture(
  currencies: CurrencyAttributes[],
  now = new Date('2025-03-25T15:11:18.457Z'),
): TransactionAttributes[] {
  const bases = [
    { label: 'Connexion to my account', points: 15 },
    { label: 'Watched the new Youtube ad', points: 10 },
    { label: 'Played the daily game', points: 10 },
    { label: '-25% e-commerce discount obtained', points: -100 },
    { label: 'Has checked the new catalogue', points: 20 },
    { label: 'Played the daily game', points: 10 },
    { label: 'Played the daily game', points: 10 },
    { label: '-15% e-commerce discount obtained', points: -60 },
    { label: 'Played the daily game', points: 10 },
  ];

  return bases.map(
    ({ label, points }, index) =>
      ({
        id: `97b82494-f435-4156-8f03-d53e4bbfdf6${index}`,
        accountId,
        programId: programIdFixture,
        profileId,
        i18nAttributes: {
          en: {
            description: label,
          },
          fr: {
            description: label,
          },
          nl: {
            description: label,
          },
        },
        currencyId: currencies[index % currencies.length]?.id,
        amount: points,
        quantityUsed: 0,
        eventTimestamp: subDays(now, 7 * index)?.toJSON(),
        expirationDate: addDays(now, -7 * index + 21)?.toJSON(),
      } as DeepPartial<TransactionAttributes>),
  ) as TransactionAttributes[];
}

export function buildLeaderboardFixture(currencies = CurrenciesFixture): {
  [currencyId: string]: Leaderboard;
} {
  return Object.fromEntries(
    currencies.map(currency => [
      currency.id,
      {
        top: [
          {
            id: '92781f06-4331-4af5-9515-7f7fff4e3220',
            position: 1,
            score: 1510154,
            profile: {
              firstName: 'Jacqueline',
              lastName: 'Raymond',
              picture: 'https://i.pravatar.cc/30?img=1',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'b5d8a876-93f6-4384-8fee-af98e19a39b4',
            position: 2,
            score: 1256587,
            profile: {
              firstName: 'Adam',
              lastName: 'Gordon',
              picture: 'https://i.pravatar.cc/30?img=2',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '9b791411-9b4e-492a-93db-5a621aba092b',
            position: 3,
            score: 1102456,
            profile: {
              firstName: 'Stefano',
              lastName: 'Cruz',
              picture: 'https://i.pravatar.cc/30?img=9',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'add944fd-5aa2-425a-91cd-26df9f1ec3f1',
            position: 4,
            score: 1092456,
            profile: {
              firstName: 'Alexander',
              lastName: 'Son',
              picture: 'https://i.pravatar.cc/30?img=10',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'c932d2a8-b304-4765-8614-f443ebbe6696',
            position: 5,
            score: 992539,
            profile: {
              firstName: 'Mariette',
              lastName: 'Heinz',
              picture: 'https://i.pravatar.cc/30?img=19',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'be644f39-5082-44e7-b36b-2f777540e6c9',
            position: 6,
            score: 975112,
            profile: {
              firstName: 'Enzo',
              lastName: 'Chiffon',
              picture: 'https://i.pravatar.cc/30?img=20',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'b52e4661-b843-47e8-b4e1-06f78e77ae44',
            position: 7,
            score: 862925,
            profile: {
              firstName: 'Roberto',
              lastName: 'Aliagas',
              picture: 'https://i.pravatar.cc/30?img=21',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '177f8148-2096-4008-b8f6-48d34be3b2db',
            position: 8,
            score: 752142,
            profile: {
              firstName: 'Gilles',
              lastName: 'Tonic',
              picture: 'https://i.pravatar.cc/30?img=22',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '25cf7b6f-d337-42f2-8e58-f74cfa6bc6e5',
            position: 9,
            score: 626123,
            profile: {
              firstName: 'Marc',
              lastName: 'Tampion',
              picture: 'https://i.pravatar.cc/30?img=23',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '4600b945-34be-48f2-b4b1-60fa7823c9a1',
            position: 10,
            score: 500000,
            profile: {
              firstName: 'Norbert',
              lastName: 'Martin',
              picture: 'https://i.pravatar.cc/30?img=24',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
        ],
        above: [
          {
            id: '9332dbf9-d774-402b-ab34-fece96ebf14b',
            position: 114,
            score: 35152,
            profile: {
              firstName: 'Georgette',
              lastName: 'Cohen',
              picture: 'https://i.pravatar.cc/30?img=4',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '67386656-2bc3-4d97-b121-8beb833c7690',
            position: 115,
            score: 34142,
            profile: {
              firstName: 'Julien',
              lastName: 'Thomas',
              picture: 'https://i.pravatar.cc/30?img=5',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '891cefc9-9087-4de1-9413-b40510a5629f',
            position: 116,
            score: 33132,
            profile: {
              firstName: 'Jean',
              lastName: 'Bastin',
              picture: 'https://i.pravatar.cc/30?img=26',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'd5be893a-5a12-41c7-aba7-2093a4708d44',
            position: 117,
            score: 32122,
            profile: {
              firstName: 'Herbert',
              lastName: 'Caraban',
              picture: 'https://i.pravatar.cc/30?img=27',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'c5d58b99-cddb-4f06-95b5-a970f481477a',
            position: 118,
            score: 31111,
            profile: {
              firstName: 'Aurélie',
              lastName: 'Praat',
              picture: 'https://i.pravatar.cc/30?img=28',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'cbb745db-6026-4d56-849d-25c70d794d12',
            position: 119,
            score: 30101,
            profile: {
              firstName: 'Amaury',
              lastName: 'Thomas',
              picture: 'https://i.pravatar.cc/30?img=29',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'b42ae1ee-aa06-402b-a412-085a5cf360c0',
            position: 120,
            score: 29152,
            profile: {
              firstName: 'Luce',
              lastName: 'Valmont',
              picture: 'https://i.pravatar.cc/30?img=30',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'ca04419f-0607-41f5-bee2-6d4bd2521697',
            position: 121,
            score: 28138,
            profile: {
              firstName: 'Céline',
              lastName: 'Aubert',
              picture: 'https://i.pravatar.cc/30?img=31',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'c3028f70-1ef0-46dc-9f6b-de7c4e2e04ea',
            position: 122,
            score: 27111,
            profile: {
              firstName: 'Damien',
              lastName: 'Toussaint',
              picture: 'https://i.pravatar.cc/30?img=32',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '301fa766-6b6b-4e51-85bd-bad830596d50',
            position: 123,
            score: 26912,
            profile: {
              firstName: 'Paul',
              lastName: 'Poiret',
              picture: 'https://i.pravatar.cc/30?img=33',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '807eb534-f298-4f51-8eed-db36f77f047b',
            position: 124,
            score: 25811,
            profile: {
              firstName: 'Nicole',
              lastName: 'Hamon',
              picture: 'https://i.pravatar.cc/30?img=34',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '1036b03f-f09d-4637-bf19-54e8f63cceaf',
            position: 125,
            score: 24680,
            profile: {
              firstName: 'François',
              lastName: 'Leroy',
              picture: 'https://i.pravatar.cc/30?img=35',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
        ],
        me: [
          {
            id: profileId,
            position: 126,
            score: 23456,
            profile: {
              firstName: 'David',
              lastName: 'Rossi',
              picture: 'https://i.pravatar.cc/160?img=3',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
        ],
        below: [
          {
            id: '48262c19-4335-484b-b93f-96d3bf25d83b',
            position: 127,
            score: 22956,
            profile: {
              firstName: 'Anastasia',
              lastName: 'Richter',
              picture: 'https://i.pravatar.cc/30?img=7',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'a2083281-488e-408e-bb52-c858c4f4ebd4',
            position: 128,
            score: 22765,
            profile: {
              firstName: 'Jacques',
              lastName: 'Tobin',
              picture: 'https://i.pravatar.cc/30?img=8',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'cb0a150c-ea9e-424c-9466-c6d7841b0c8a',
            position: 129,
            score: 21098,
            profile: {
              firstName: 'Alex',
              lastName: 'Anderson',
              picture: 'https://i.pravatar.cc/30?img=11',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'a257a5f4-3ebe-4973-b8ac-af45f6bb7b53',
            position: 130,
            score: 20864,
            profile: {
              firstName: 'Greg',
              lastName: 'Mandel',
              picture: 'https://i.pravatar.cc/30?img=12',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '5c6a0ce2-5af2-4f65-822e-5beeda286433',
            position: 131,
            score: 19876,
            profile: {
              firstName: 'Geo',
              lastName: 'Lee',
              picture: 'https://i.pravatar.cc/30?img=14',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '5f341b03-06a2-478d-9d20-1b1a6cd05263',
            position: 132,
            score: 18765,
            profile: {
              firstName: 'Peter',
              lastName: 'Parcœur',
              picture: 'https://i.pravatar.cc/30?img=15',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '5ad84786-f1b5-4d06-ba06-d33100df7a6c',
            position: 133,
            score: 17654,
            profile: {
              firstName: 'Peter',
              lastName: 'Minet',
              picture: 'https://i.pravatar.cc/30?img=16',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: 'a0148194-842c-4961-bbbc-e22285a432d7',
            position: 134,
            score: 1234,
            profile: {
              firstName: 'Arthur',
              lastName: 'De Bretagne',
              picture: 'https://i.pravatar.cc/30?img=17',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '928629da-1c4f-41fd-a43e-b6b238fe1a2e',
            position: 135,
            score: 321,
            profile: {
              firstName: 'Laurent',
              lastName: 'Lamonnaie',
              picture: 'https://i.pravatar.cc/30?img=18',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
          {
            id: '32f9291e-7f3d-49da-8b2a-7a0e53ced8e5',
            position: 136,
            score: 42,
            profile: {
              firstName: 'Leia',
              lastName: 'Lechien',
              picture:
                'https://slack-imgs.com/?c=1&o1=gu&url=https%3A%2F%2Femoji.slack-edge.com%2FT053A9D2T%2Fleia%2Fdfc2e100eafa8724.png',
            },
          } as Partial<LeaderboardEntry> as LeaderboardEntry,
        ],
      },
    ]),
  );
}

export function makeRewardsFixture(currencies: CurrencyAttributes[]): CompleteImpactAttributes {
  return {
    rules: [
      {
        id: '76ab7318-aab5-4edc-bccd-4939e34ad36f',
        currency: {
          value: 4000,
          id: currencies[Math.floor(Math.random() * currencies.length)].id,
        },
        state: RewardListRuleState.NOT_ENOUGH_FUNDS,
        quantity: 5,
        reward: {
          id: 'a9ff27b2-5a71-42a8-9e7d-95ba8c195d3d',
          i18nAttributes: {
            en: {
              name: 'Lorem ipsum 1',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            fr: {
              name: 'Lorem ipsum 1',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            nl: {
              name: 'Lorem ipsum 1',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
          },
        },
      },
      {
        id: '8b91d70a-8301-4fcc-b475-66e6eab9ac58',
        currency: {
          value: 30000,
          id: currencies[Math.floor(Math.random() * currencies.length)].id,
        },
        state: RewardListRuleState.AVAILABLE,
        quantity: 7,
        reward: {
          id: '11d69a9b-810d-4987-85bc-488e3aa04d74',
          i18nAttributes: {
            en: {
              name: 'Lorem ipsum 2',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            fr: {
              name: 'Lorem ipsum 2',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            nl: {
              name: 'Lorem ipsum 2',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
          },
        },
      },
      {
        id: '3ffbb964-7cc9-4f64-b60a-8646bdffe0d1',
        currency: {
          value: 16000,
          id: currencies[Math.floor(Math.random() * currencies.length)].id,
        },
        state: RewardListRuleState.AVAILABLE,
        quantity: 2,
        reward: {
          id: '24484672-de94-4fba-9683-5b997035afb0',
          i18nAttributes: {
            en: {
              name: 'Lorem ipsum 3',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            fr: {
              name: 'Lorem ipsum 3',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            nl: {
              name: 'Lorem ipsum 3',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
          },
        },
      },
      {
        id: '2cc05876-be13-47f3-831b-b9917602ad69',
        currency: {
          value: 1000,
          id: currencies[Math.floor(Math.random() * currencies.length)].id,
        },
        state: RewardListRuleState.SOLD_OUT,
        quantity: 0,
        reward: {
          id: '3c5aea4b-6a7d-462f-8aa6-82512e01d642',
          i18nAttributes: {
            en: {
              name: 'Lorem ipsum 4',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            fr: {
              name: 'Lorem ipsum 4',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            nl: {
              name: 'Lorem ipsum 4',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
          },
        },
      },
      {
        id: '824b322d-711b-4e62-8c7d-ff70f410a6ef',
        currency: {
          value: 12000,
          id: currencies[Math.floor(Math.random() * currencies.length)].id,
        },
        state: RewardListRuleState.AVAILABLE,
        quantity: 10,
        reward: {
          id: '3701deac-ed00-4e64-b3ae-7b9ad3b427c8',
          i18nAttributes: {
            en: {
              name: 'Lorem ipsum 5',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            fr: {
              name: 'Lorem ipsum 5',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
            nl: {
              name: 'Lorem ipsum 5',
              description:
                '<p>FR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ut egestas ante, sed varius nibh. Pellentesque ac sem eget urna lobortis placerat ut quis velit. Etiam ex odio, malesuada sit amet massa a, elementum semper leo.</p>',
              image: '',
            },
          },
        },
      },
    ],
  } as unknown as CompleteImpactAttributes;
}
